import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import omit from 'lodash/omit';
import ShareHelper from 'legacy-packages/unit-outline/src/components/_shared/ShareHelper';
import AssignHelper from 'legacy-packages/unit-outline/src/components/_shared/AssignHelper';
import withCourseOutline from 'chameleon-packages/units/composable/withCourseOutline';
import getCourseOutlineResourcesByType from 'legacy-src/utility/helpers/getCourseOutlineResourcesByType';
import findResourceInCourseOutline from 'legacy-src/utility/helpers/findResourceInCourseOutline';
import VideoResourcePreview from 'legacy-src/components/VideoResourcePreview';
import ResourceTypes from 'legacy-packages/unit-outline/src/constants';
import { updateSharedResourceWatchStatus } from 'legacy-src/actions/sharedResources';
import setAPDCarouselData, {
  updateSingleVideoAPDCarouselData,
} from 'legacy-src/actions/apdCarousel';
import { isEmpty } from 'lodash';
import { VideoResourceWrapperCommonProps } from 'chameleon-src/components/VideoResourcePreviewWrapper/commonPropsHOC';

const getResourceLabel = (resource) => {
  return resource.displayName.split(':')[0];
};

const getSortedIndex = (resourceLabel, sortedLabels, isTopic) => {
  for (let i = 0; i < sortedLabels.length; i++) {
    const label = sortedLabels[i];
    const isLabelFound = isTopic
      ? resourceLabel === label
      : resourceLabel.startsWith(label);
    if (isLabelFound) {
      return i;
    }
  }
  return -1;
};

const VideoResourcePreviewWrapper = (props) => {
  const {
    location,
    router,
    userData,
    subjectData,
    userClasses,
    setSharedResourceWatchStatus,
    searchedResources,
    setAPDCarouselData: setAPDCarouselDataProp,
    apdCarouselData,
    updateSingleVideoAPDCarouselData: updateSingleVideoAPDCarouselDataProp,
    courseOutline,
    currentUnit,
    teacherClasses,
    apOutlineSearchFlag,
    showZeroWatchedFlag,
    studentClasses,
    isImpersonating,
    onAssign,
    sortedLabels,
    category,
  } = props;
  const { query, pathname } = location || { query: {} };
  const { apd: videoId, searchTerm } = query;

  const { loading, units = [] } = courseOutline || {};

  const videoResource =
    (videoId && findResourceInCourseOutline(videoId, units)) || null;
  const isVideoAvailable = !!videoResource;
  const allAvailableVideoResources = useMemo(() => {
    let resources = getCourseOutlineResourcesByType(
      units,
      ResourceTypes.EMBEDDED_VIDEO,
    );
    const isTopic = category === 'Topic';
    if (sortedLabels && sortedLabels.length > 0) {
      resources = resources.filter((resource) => {
        const resourceLabel = getResourceLabel(resource);
        return sortedLabels.some((label) =>
          isTopic ? resourceLabel === label : resourceLabel.startsWith(label),
        );
      });
      resources.sort((a, b) => {
        const aResourceLabel = getResourceLabel(a);
        const bResourceLabel = getResourceLabel(b);
        return (
          getSortedIndex(aResourceLabel, sortedLabels, isTopic) -
          getSortedIndex(bResourceLabel, sortedLabels, isTopic)
        );
      });
    }
    return resources;
  }, [units, sortedLabels]);

  if (!isVideoAvailable) {
    return null;
  }

  const { resources, isFetching, isDefault } = searchedResources || {};
  const filteredResources = (resources || [])
    .filter(
      ({ resource }) => resource.__typename === ResourceTypes.EMBEDDED_VIDEO,
    )
    .map(({ resource, unit }) => ({
      ...resource,
      unitId: unit,
    }));

  const loadingFilteredResources =
    (Boolean(searchTerm) && isDefault) || isFetching;

  const setActiveUnitOnVideoChange = (activeVideoResource, unitId) => {
    if (currentUnit.number !== unitId) {
      router.push({
        pathname,
        query: {
          ...query,
          unit: unitId,
          apd: activeVideoResource.url,
        },
      });
    }
  };

  /**
   * This component was duplicated and customized for use in the SPP player, when a change is made here
   * please consider whether it would also be required in chameleon/src/components/SPPVideoResourcePreviewWrapper/index.js
   *
   * At the very least ask POs (Nuri + Rolling Stones) if the change here should apply to the SPP player as well. If it should
   * apply to both, then maybe putting it in the change in the VideoResourcePreview + VideoResourceWrapperCommonProps
   * components may be a better approach.
   *
   * A similar reminder for maintainers has been added in the other component as well.
   */
  return (
    <VideoResourcePreview
      videoModalQuery={omit(query, 'apd')}
      loadingData={loading}
      videoId={videoId}
      videoResource={videoResource}
      userData={userData}
      subject={subjectData}
      userClasses={userClasses}
      router={router}
      sections={teacherClasses}
      updateSharedResourceWatchStatus={setSharedResourceWatchStatus}
      withCarousel
      useFilterableResources={apOutlineSearchFlag}
      allAvailableVideoResources={allAvailableVideoResources}
      onVideoSelection={!isEmpty(currentUnit) && setActiveUnitOnVideoChange}
      studentClasses={studentClasses}
      filteredResources={filteredResources}
      loadingFilteredResources={loadingFilteredResources}
      setAPDCarouselData={setAPDCarouselDataProp}
      apdCarouselData={apdCarouselData}
      updateSingleVideoAPDCarouselData={updateSingleVideoAPDCarouselDataProp}
      filteredResourcesLabel="Videos Matching Search Criteria"
      showZeroWatchedFlag={showZeroWatchedFlag}
      isImpersonating={isImpersonating}
      onAssign={onAssign}
    />
  );
};

VideoResourcePreviewWrapper.defaultProps = {
  currentUnit: {},
  sortedLabels: [],
  category: '',
};

VideoResourcePreviewWrapper.propTypes = {
  currentUnit: PropTypes.object,
  category: PropTypes.string,
};

export default compose(
  connect(
    (state) => ({
      apdCarouselData: state?.apdCarouselData,
    }),
    {
      setSharedResourceWatchStatus: updateSharedResourceWatchStatus,
      setAPDCarouselData,
      updateSingleVideoAPDCarouselData,
    },
  ),
  VideoResourceWrapperCommonProps,
  withCourseOutline(),
  ShareHelper,
  AssignHelper,
)(VideoResourcePreviewWrapper);
